// Mobile variant of StyleOutfit — Mody-inspired Android-native UI.
// Scene strip for vibes · 3-col grid for outfits & items · editorial section headers · sticky bottom CTA.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Wand2, Coins } from "lucide-react";

import { CreditsChip } from "@/components/CreditsChip";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { HighDemandDialog } from "@/components/HighDemandDialog";
import { GenerationOverlay } from "@/components/GenerationOverlay";
import { BottomSheet } from "@/components/BottomSheet";
import { PaywallModal } from "@/components/PaywallModal";
import { Button } from "@/components/ui/button";
import { GENDER_OPTIONS, VIBE_CATEGORY_LABELS, type VibeCategory } from "@/lib/style-types";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { MobileShell, SectionHeader, SceneStrip, EditCard, StickyCTA } from "@/components/mobile";
import { StylePreviewSheet } from "@/components/mobile/StylePreviewSheet";
import { FeaturedStrip } from "@/components/mobile/FeaturedStrip";
import { SubmitFeaturedLauncher } from "@/components/featured/SubmitFeaturedLauncher";
import { useFeaturedSubmissions, type FeaturedItem } from "@/hooks/useFeaturedSubmissions";
import { Crown } from "lucide-react";

import { VIBES } from "@/lib/vibes";
import { OUTFIT_LOOKS } from "@/lib/outfit-looks";
import { SINGLE_ITEMS } from "@/lib/single-items";
import { toast } from "sonner";
import { STAGES, type StyleOutfitCtx, type Tab } from "./useStyleOutfit";

export const StyleOutfitMobile = (ctx: StyleOutfitCtx) => {
  const {
    tab, setTab, gender, setGender,
    outfitCategory, setOutfitCategory, itemType, setItemType,
    userPhotoPreview, busy, stageIdx, genError,
    
    buyOpen, setBuyOpen, buyDefaultPack, setBuyDefaultPack,
    outOfCreditsOpen, setOutOfCreditsOpen,
    highDemandOpen, setHighDemandOpen,
    paywall, setPaywall, handlePaywallDismiss, handlePaywallBuy,
    previewVibe, setPreviewVibe, previewLook, setPreviewLook, previewItem, setPreviewItem,
    customItemPreview, customItemSheetOpen, setCustomItemSheetOpen,
    setCustomItemFile, setCustomItemPreview,
    customItemLabel, setCustomItemLabel,
    vibeMode, setVibeMode,
    visibleVibes, visibleOutfits, visibleItems,
    outfitCategoryChips, itemTypeChips,
    onPickPhoto,
    openVibe, confirmVibe, confirmLook, confirmItem, confirmCustomItem,
    generateOutfit, setGenError, lastAttemptRef,
  } = ctx;

  // Featured user-submitted looks at the top of the page.
  // Strip is Android-only; submission launcher is available everywhere.
  // Mobile web users now see the Featured strip too (was Android-only).
  const showFeatured = true;
  const { items: featuredItems } = useFeaturedSubmissions(showFeatured);
  const [featuredLauncherOpen, setFeaturedLauncherOpen] = useState(false);

  const reuseFeatured = (it: FeaturedItem) => {
    const label = (it.template_label ?? "").toLowerCase();
    if (it.kind === "vibe") {
      const v = VIBES.find((x) => x.label.toLowerCase() === label);
      if (v) { openVibe(v); return; }
    } else if (it.kind === "look") {
      const l = OUTFIT_LOOKS.find((x) => x.label.toLowerCase() === label);
      if (l) { setPreviewLook(l); return; }
    } else if (it.kind === "item") {
      const i = SINGLE_ITEMS.find((x) => x.label.toLowerCase() === label);
      if (i) { setPreviewItem(i); return; }
    }
    toast.message("Template not available", {
      description: "We couldn't find this exact template anymore — pick from the lists below.",
    });
  };

  // Deep-link from Aura reels "Try it" → auto-open the matching preview sheet.
  const location = useLocation();
  const navigate = useNavigate();
  const honoredReuseRef = useRef(false);
  useEffect(() => {
    if (honoredReuseRef.current) return;
    const state = location.state as
      | { reuseFeatured?: { kind: string; template_label: string | null } }
      | null;
    const r = state?.reuseFeatured;
    if (!r) return;
    honoredReuseRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    const label = (r.template_label ?? "").toLowerCase();
    if (r.kind === "vibe") {
      const v = VIBES.find((x) => x.label.toLowerCase() === label);
      if (v) { setTab("vibes"); openVibe(v); return; }
    } else if (r.kind === "look") {
      const l = OUTFIT_LOOKS.find((x) => x.label.toLowerCase() === label);
      if (l) { setTab("outfits"); setPreviewLook(l); return; }
    } else if (r.kind === "item") {
      const i = SINGLE_ITEMS.find((x) => x.label.toLowerCase() === label);
      if (i) { setTab("items"); setPreviewItem(i); return; }
    }
    toast.message("Template not available", {
      description: "Pick a similar template from the lists below.",
    });
  }, [location, navigate, openVibe, setPreviewItem, setPreviewLook, setTab]);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "vibes", label: "Vibes" },
    { id: "outfits", label: "Outfits" },
    { id: "items", label: "Items" },
  ];

  // Auto-center the active chip in the horizontal filter rows (mirrors TryOn behavior).
  const outfitChipRowRef = useRef<HTMLDivElement | null>(null);
  const outfitChipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const itemChipRowRef = useRef<HTMLDivElement | null>(null);
  const itemChipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const vibeChipRowRef = useRef<HTMLDivElement | null>(null);
  const vibeChipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Local filter for the Scenes/Vibes tab — narrows visibleVibes by category.
  const [vibeCategory, setVibeCategory] = useState<VibeCategory | "all">("all");
  const vibeCategoryChips = useMemo(() => {
    const present = new Set<VibeCategory>();
    for (const v of visibleVibes) {
      if (v.category) present.add(v.category);
    }
    return (Object.keys(VIBE_CATEGORY_LABELS) as VibeCategory[])
      .filter((k) => present.has(k))
      .map((k) => ({ id: k, ...VIBE_CATEGORY_LABELS[k] }));
  }, [visibleVibes]);
  const filteredVibes = useMemo(() => {
    if (vibeCategory === "all") return visibleVibes;
    return visibleVibes.filter((v) => v.category === vibeCategory);
  }, [visibleVibes, vibeCategory]);

  const centerChip = (
    rowRef: React.MutableRefObject<HTMLDivElement | null>,
    chipMap: React.MutableRefObject<Record<string, HTMLButtonElement | null>>,
    key: string,
  ) => {
    requestAnimationFrame(() => {
      const row = rowRef.current;
      const chip = chipMap.current[key];
      if (!row || !chip) return;
      const target = chip.offsetLeft - row.clientWidth / 2 + chip.clientWidth / 2;
      row.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    });
  };

  const handleOutfitCat = (key: typeof outfitCategory) => {
    setOutfitCategory(key);
    centerChip(outfitChipRowRef, outfitChipRefs, String(key));
  };
  const handleItemType = (key: typeof itemType) => {
    setItemType(key);
    centerChip(itemChipRowRef, itemChipRefs, String(key));
  };
  const handleVibeCat = (key: VibeCategory | "all") => {
    setVibeCategory(key);
    centerChip(vibeChipRowRef, vibeChipRefs, String(key));
  };

  return (
    <>
      <SEO />
      <MobileShell
        title="Style Lab"
        actions={<CreditsChip onClick={() => setBuyOpen(true)} />}
        withStickyCTA
      >
        <p className="mt-3 text-[12px] text-muted-foreground">
          Pick a vibe, outfit, or single piece — we'll add your photo on the next step.
        </p>

        {/* ── Segmented tabs (Vibes · Outfits · Items · Custom) ─────────────── */}
        <div className="mt-4 grid grid-cols-3 rounded-full border border-white/10 bg-muted/30 p-1">
          {tabs.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={cn(
                "rounded-full px-2 py-2 text-[11px] font-semibold transition-all active:scale-95",
                tab === t.id ? "bg-foreground text-background shadow-sm" : "text-muted-foreground",
              )}
            >{t.label}</button>
          ))}
        </div>

        {/* ── Gender filter — only on outfits/items ─────────────────────────── */}
        {tab !== "vibes" && (
          <div className="mt-3 flex gap-2">
            {GENDER_OPTIONS.map((g) => (
              <button key={g.id} type="button" onClick={() => setGender(g.id)}
                className={cn(
                  "flex-1 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-all active:scale-95",
                  gender === g.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-white/10 bg-transparent text-muted-foreground",
                )}
              ><span className="mr-1">{g.emoji}</span>{g.label}</button>
            ))}
          </div>
        )}

        {/* ── Sub-filters ──────────────────────────────────────────────────── */}
        {tab === "outfits" && outfitCategoryChips.length > 0 && (
          <div
            ref={outfitChipRowRef}
            className="mt-3 -mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <Chip
              chipRef={(el) => { outfitChipRefs.current["all"] = el; }}
              active={outfitCategory === "all"}
              onClick={() => handleOutfitCat("all")}
            >
              All
            </Chip>
            {outfitCategoryChips.map((c) => (
              <Chip
                key={c.id}
                chipRef={(el) => { outfitChipRefs.current[c.id] = el; }}
                active={outfitCategory === c.id}
                onClick={() => handleOutfitCat(c.id)}
              >
                <span className="mr-1">{c.emoji}</span>{c.label}
              </Chip>
            ))}
          </div>
        )}
        {tab === "items" && itemTypeChips.length > 0 && (
          <div
            ref={itemChipRowRef}
            className="mt-3 -mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <Chip
              chipRef={(el) => { itemChipRefs.current["all"] = el; }}
              active={itemType === "all"}
              onClick={() => handleItemType("all")}
            >
              All
            </Chip>
            {itemTypeChips.map((c) => (
              <Chip
                key={c.id}
                chipRef={(el) => { itemChipRefs.current[c.id] = el; }}
                active={itemType === c.id}
                onClick={() => handleItemType(c.id)}
              >
                <span className="mr-1">{c.emoji}</span>{c.label}
              </Chip>
            ))}
          </div>
        )}

        {/* ── Vibes — horizontal scene strip + grid below ──────────────────── */}
        {tab === "vibes" && (
          <>
            {/* Category filter chips — auto-center the picked chip like TryOn */}
            <div
              ref={vibeChipRowRef}
              className="mt-3 -mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <Chip
                chipRef={(el) => { vibeChipRefs.current["all"] = el; }}
                active={vibeCategory === "all"}
                onClick={() => handleVibeCat("all")}
              >
                All
              </Chip>
              {vibeCategoryChips.map((c) => (
                <Chip
                  key={c.id}
                  chipRef={(el) => { vibeChipRefs.current[c.id] = el; }}
                  active={vibeCategory === c.id}
                  onClick={() => handleVibeCat(c.id)}
                >
                  <span className="mr-1">{c.emoji}</span>{c.label}
                </Chip>
              ))}
            </div>

            <SectionHeader
              title="Featured"
              subtitle="Real looks from the community"
              action={
                <button
                  type="button"
                  onClick={() => setFeaturedLauncherOpen(true)}
                  className="flex items-center gap-1 rounded-full bg-gradient-to-r from-primary to-accent px-3 py-1.5 text-[11px] font-bold text-primary-foreground shadow-soft active:scale-95"
                >
                  <Crown className="h-3.5 w-3.5" /> Get Featured
                </button>
              }
            />
            {showFeatured && featuredItems.length > 0 && (
              <FeaturedStrip items={featuredItems} onReuse={reuseFeatured} />
            )}

            <SectionHeader title="Scenes" subtitle="Drop yourself into a place" />
            <SceneStrip
              items={filteredVibes.map((v) => ({ id: v.id, label: v.label, image: v.image }))}
              onPick={(id) => {
                const v = filteredVibes.find((x) => x.id === id);
                if (v) openVibe(v);
              }}
            />
            <SectionHeader
              title="All Vibes"
              subtitle="Pick anyone for a try-on"
              action={<span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Coins className="h-3 w-3" />1 credit</span>}
            />
            {filteredVibes.length === 0 ? (
              <Empty text="No vibes in this category yet." />
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {filteredVibes.map((v) => (
                  <EditCard
                    key={v.id}
                    image={v.image}
                    label={v.label}
                    onClick={() => openVibe(v)}
                    badge={!v.genders.includes("men") ? "Women only" : undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Outfits — full looks ─────────────────────────────────────────── */}
        {tab === "outfits" && (
          <>
            <SectionHeader
              title="Outfits"
              subtitle="Full looks · face & pose stay identical"
              action={<span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Coins className="h-3 w-3" />1 credit</span>}
            />
            {visibleOutfits.length === 0 ? (
              <Empty text="No outfits in this category yet." />
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {visibleOutfits.map((l) => (
                  <EditCard key={l.id} image={l.image} label={l.label} onClick={() => setPreviewLook(l)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Items — single-piece swap ────────────────────────────────────── */}
        {tab === "items" && (
          <>
            <SectionHeader
              title="Single Items"
              subtitle="Swap one piece · everything else stays"
              action={<span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Coins className="h-3 w-3" />1 credit</span>}
            />
            {visibleItems.length === 0 ? (
              <Empty text="No items here yet." />
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {visibleItems.map((i) => (
                  <EditCard key={i.id} image={i.image} label={i.label} onClick={() => setPreviewItem(i)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Overlays & sheets ────────────────────────────────────────────── */}
        <GenerationOverlay
          open={busy || !!genError}
          stage={STAGES[stageIdx]}
          errorMessage={genError}
          onRetry={() => { setGenError(null); lastAttemptRef.current?.(); }}
          onCancel={() => setGenError(null)}
        />

        {/* ── Photo-first preview (vibe / look / item) ─────────────────────── */}
        <StylePreviewSheet
          open={!!previewVibe}
          onClose={() => setPreviewVibe(null)}
          kind="vibe"
          template={previewVibe}
          userPhotoPreview={userPhotoPreview}
          onPickPhoto={onPickPhoto}
          onConfirm={confirmVibe}
          vibeMode={vibeMode}
          setVibeMode={setVibeMode}
        />
        <StylePreviewSheet
          open={!!previewLook}
          onClose={() => setPreviewLook(null)}
          kind="look"
          template={previewLook}
          userPhotoPreview={userPhotoPreview}
          onPickPhoto={onPickPhoto}
          onConfirm={confirmLook}
        />
        <StylePreviewSheet
          open={!!previewItem}
          onClose={() => setPreviewItem(null)}
          kind="item"
          template={previewItem}
          userPhotoPreview={userPhotoPreview}
          onPickPhoto={onPickPhoto}
          onConfirm={confirmItem}
        />

        <BottomSheet
          open={customItemSheetOpen}
          onClose={() => { setCustomItemSheetOpen(false); setCustomItemFile(null); setCustomItemPreview(null); }}
          title="Try your own item"
        >
          {customItemPreview && (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-2xl bg-muted">
                <img src={customItemPreview} alt="Your item" className="aspect-square w-full object-contain p-4" />
              </div>
              <input id="custom-item-label" name="customItemLabel" autoComplete="off" type="text" maxLength={40}
                value={customItemLabel} onChange={(e) => setCustomItemLabel(e.target.value)}
                className="h-11 w-full rounded-full border border-input bg-background px-4 text-sm" placeholder="e.g. My new jacket" />
              <Button className="h-12 w-full rounded-full gradient-primary text-primary-foreground" onClick={confirmCustomItem}>
                <Wand2 className="mr-2 h-4 w-4" /> Try it on · 1 credit
              </Button>
            </div>
          )}
        </BottomSheet>

        <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} defaultPackId={buyDefaultPack} />
        <BuyCreditsDialog open={outOfCreditsOpen} onOpenChange={setOutOfCreditsOpen} outOfCredits />
        <HighDemandDialog open={highDemandOpen} onOpenChange={setHighDemandOpen} />

        <PaywallModal
          open={!!paywall} variant={paywall}
          onClose={() => setPaywall(null)} onDismiss={handlePaywallDismiss}
          onBuy={handlePaywallBuy}
        />

        <SubmitFeaturedLauncher
          open={featuredLauncherOpen}
          onOpenChange={setFeaturedLauncherOpen}
        />
      </MobileShell>
    </>
  );
};

const Chip = ({ active, onClick, children, chipRef }: { active: boolean; onClick: () => void; children: React.ReactNode; chipRef?: (el: HTMLButtonElement | null) => void }) => (
  <button ref={chipRef} type="button" onClick={onClick}
    className={cn(
      "shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-all active:scale-95",
      active ? "border-primary bg-primary text-primary-foreground" : "border-white/10 bg-transparent text-muted-foreground",
    )}
  >{children}</button>
);


const Empty = ({ text }: { text: string }) => (
  <div className="rounded-2xl border border-dashed border-white/10 bg-muted/10 p-8 text-center">
    <p className="text-sm text-muted-foreground">{text}</p>
  </div>
);
